import { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Users, Plus, Trash2, UserCog, Shield, Sparkles, Building2, Upload, FileText, Download, CheckCircle, XCircle, Edit, Eye, PenLine, Trash, Grid3X3, Table2, Lock, Hash, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

const ALL_PERMISSIONS = [
  { id: 'view_dashboard', label: 'View Dashboard' },
  { id: 'manage_assets', label: 'Manage Assets' },
  { id: 'manage_threats', label: 'Manage Threats' },
  { id: 'manage_scans', label: 'Manage Scans' },
  { id: 'view_vulnerabilities', label: 'View Vulnerabilities' },
  { id: 'manage_vulnerabilities', label: 'Manage Vulnerabilities' },
  { id: 'view_compliance', label: 'View Compliance' },
  { id: 'run_compliance', label: 'Run Compliance Checks' },
  { id: 'view_audit_logs', label: 'View Audit Logs' },
  { id: 'manage_users', label: 'Manage Users' },
  { id: 'manage_roles', label: 'Manage Roles' },
];

// Granular Page Permissions - View/Write/Edit/Delete per page
const PAGE_PERMISSIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
  { id: 'strategic_dashboard', label: 'Strategic Dashboard', icon: 'BarChart3' },
  { id: 'operational_dashboard', label: 'Operational Dashboard', icon: 'Activity' },
  { id: 'threat_intel', label: 'Threat Intelligence', icon: 'Globe' },
  { id: 'threat_dashboard', label: 'Global Threat Map', icon: 'Map' },
  { id: 'threat_hunting', label: 'Threat Hunting Playbooks', icon: 'Search' },
  { id: 'exploit_intel', label: 'Exploit Intelligence', icon: 'TrendingUp' },
  { id: 'attack_paths', label: 'Attack Paths', icon: 'GitBranch' },
  { id: 'asset_management', label: 'Asset Management', icon: 'Server' },
  { id: 'assessments', label: 'Assessment Center', icon: 'Shield' },
  { id: 'compliance', label: 'Compliance', icon: 'FileCheck' },
  { id: 'compliance_reporting', label: 'Compliance Reporting', icon: 'FileText' },
  { id: 'raptor', label: 'SecureProbe (RAPTOR)', icon: 'Radar' },
  { id: 'vulnerabilities', label: 'Vulnerability Hub', icon: 'Bug' },
  { id: 'automation_center', label: 'Automation Center', icon: 'Bot' },
  { id: 'security_initiatives', label: 'Security Initiatives', icon: 'Target' },
  { id: 'reports', label: 'Reporting', icon: 'FileText' },
  { id: 'itsm', label: 'ITSM Orchestration', icon: 'Ticket' },
  { id: 'identity_exposure', label: 'Identity Exposure', icon: 'UserX' },
  { id: 'crown_jewels', label: 'Crown Jewels', icon: 'Crown' },
  { id: 'ai_insights', label: 'AI Insights', icon: 'Brain' },
  { id: 'administration', label: 'Administration', icon: 'Settings' },
  { id: 'platform_admin', label: 'Platform Admin', icon: 'Building2' },
  { id: 'registrations', label: 'Registrations', icon: 'UserPlus' },
];

const PERMISSION_ACTIONS = ['view', 'write', 'edit', 'delete'];

export default function Administration() {
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isSampleDataLoading, setIsSampleDataLoading] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Get current user from localStorage to check if platform_admin
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isPlatformAdmin = currentUser?.role === 'platform_admin';
  const isTenantAdmin = currentUser?.role === 'admin';
  
  // Tenant switching state (Platform Admin only)
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [selectedTenantUsers, setSelectedTenantUsers] = useState([]);
  const [viewingTenant, setViewingTenant] = useState(null);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'viewer',
    organization_id: '',
  });

  const [newRole, setNewRole] = useState({
    name: '',
    display_name: '',
    permissions: [],
    page_permissions: {}, // { page_id: { view: bool, write: bool, edit: bool, delete: bool } }
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    if (isPlatformAdmin) {
      fetchTenants();
    }
  }, [isPlatformAdmin]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/platform/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to fetch roles');
    }
  };

  // Fetch users from a specific tenant (Platform Admin only)
  const fetchTenantUsers = async (tenantId) => {
    if (!isPlatformAdmin || !tenantId) return;
    
    try {
      const response = await api.get(`/platform/tenants/${tenantId}/users`);
      setSelectedTenantUsers(response.data.users || []);
      setViewingTenant(response.data.tenant);
    } catch (error) {
      toast.error('Failed to fetch tenant users');
      setSelectedTenantUsers([]);
    }
  };

  // Handle tenant selection change
  const handleTenantSwitch = (tenantId) => {
    setSelectedTenantId(tenantId);
    if (tenantId) {
      fetchTenantUsers(tenantId);
    } else {
      setSelectedTenantUsers([]);
      setViewingTenant(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: newUser.email,
        password: newUser.password,
        full_name: newUser.full_name,
        role: newUser.role,
      };
      // Only include organization_id if platform admin selected one
      if (isPlatformAdmin && newUser.organization_id) {
        payload.organization_id = newUser.organization_id;
      }
      await api.post('/admin/users', payload);
      toast.success('User created successfully');
      setIsUserDialogOpen(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'viewer', organization_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/roles', newRole);
      toast.success('Role created successfully');
      setIsRoleDialogOpen(false);
      setNewRole({ name: '', display_name: '', permissions: [], page_permissions: {} });
      fetchRoles();
    } catch (error) {
      toast.error('Failed to create role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await api.delete(`/admin/roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleGenerateSampleData = async () => {
    if (!window.confirm('This will generate sample data for threats, assets, vulnerabilities, and scans. Continue?')) return;
    
    setIsSampleDataLoading(true);
    try {
      const response = await api.post('/admin/generate-sample-data');
      toast.success(`Sample data generated: ${response.data.counts.threats} threats, ${response.data.counts.assets} assets, ${response.data.counts.vulnerabilities} vulnerabilities`);
    } catch (error) {
      toast.error('Failed to generate sample data');
    } finally {
      setIsSampleDataLoading(false);
    }
  };

  const handleClearSampleData = async () => {
    if (!window.confirm('This will remove all sample/test/mock data from your organization. This action cannot be undone. Continue?')) return;
    
    setIsClearingData(true);
    try {
      const response = await api.delete('/admin/clear-sample-data');
      const deleted = response.data.deleted;
      toast.success(`Cleared: ${deleted.threats} threats, ${deleted.assets} assets, ${deleted.vulnerabilities} vulnerabilities, ${deleted.scans} scans, ${deleted.assessments} assessments`);
    } catch (error) {
      toast.error('Failed to clear sample data');
    } finally {
      setIsClearingData(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setNewRole(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handlePagePermissionToggle = (pageId, action) => {
    setNewRole(prev => {
      const currentPagePerms = prev.page_permissions[pageId] || { view: false, write: false, edit: false, delete: false };
      const newPagePerms = {
        ...currentPagePerms,
        [action]: !currentPagePerms[action]
      };
      
      // If enabling write/edit/delete, also enable view
      if ((action === 'write' || action === 'edit' || action === 'delete') && newPagePerms[action]) {
        newPagePerms.view = true;
      }
      // If disabling view, disable all other permissions
      if (action === 'view' && !newPagePerms.view) {
        newPagePerms.write = false;
        newPagePerms.edit = false;
        newPagePerms.delete = false;
      }
      
      return {
        ...prev,
        page_permissions: {
          ...prev.page_permissions,
          [pageId]: newPagePerms
        }
      };
    });
  };

  const setAllPagePermissions = (pageId, enabled) => {
    setNewRole(prev => ({
      ...prev,
      page_permissions: {
        ...prev.page_permissions,
        [pageId]: { view: enabled, write: enabled, edit: enabled, delete: enabled }
      }
    }));
  };

  // Bulk Import Functions
  const downloadTemplate = () => {
    const csvContent = 'email,full_name,password,role,organization_id\njohn@example.com,John Doe,temppass123,viewer,\njane@example.com,Jane Smith,temppass456,analyst,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkImporting(true);
    setBulkImportResults(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const users = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const user = {};
        headers.forEach((header, idx) => {
          user[header] = values[idx] || '';
        });
        return user;
      }).filter(u => u.email);

      // Call bulk import API
      const response = await api.post('/admin/users/bulk-import', { users });
      setBulkImportResults(response.data);
      
      if (response.data.successful > 0) {
        toast.success(`Imported ${response.data.successful} users successfully`);
        fetchUsers();
      }
      if (response.data.failed > 0) {
        toast.error(`Failed to import ${response.data.failed} users`);
      }
    } catch (error) {
      toast.error('Failed to process CSV file');
      setBulkImportResults({ error: error.message });
    } finally {
      setBulkImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-lg">Loading administration...</div>
      </div>
    );
  }

  return (
    <div data-testid="administration-container" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-3xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            Administration
          </h1>
          <p className={`font-inter text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>User and role management with RBAC</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="clear-sample-data-button"
            onClick={handleClearSampleData}
            disabled={isClearingData}
            variant="outline"
            className={`text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            {isClearingData ? 'Clearing...' : 'Clear Sample Data'}
          </Button>
          <Button
            data-testid="generate-sample-data-button"
            onClick={handleGenerateSampleData}
            disabled={isSampleDataLoading}
            className="bg-violet-600 hover:bg-violet-500 font-rajdhani shadow-neon-indigo text-sm text-white"
          >
            <Sparkles className="w-3.5 h-3.5 mr-2" />
            {isSampleDataLoading ? 'Generating...' : 'Generate Sample Data'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className={isDarkMode ? 'bg-zinc-900 border border-white/10' : 'bg-slate-100 border border-slate-200'}>
          <TabsTrigger value="users" data-testid="users-tab" className="text-xs">
            <Users className="w-3.5 h-3.5 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="roles-tab" className="text-xs">
            <Shield className="w-3.5 h-3.5 mr-2" />
            Roles & Permissions
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab" className="text-xs">
            <Lock className="w-3.5 h-3.5 mr-2" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-5">
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-5">
              <h2 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>User Management</h2>
              
              <div className="flex items-center gap-3">
                {/* Tenant Switcher for Platform Admin */}
                {isPlatformAdmin && tenants.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>View Tenant:</span>
                    <Select value={selectedTenantId || 'my-org'} onValueChange={(val) => handleTenantSwitch(val === 'my-org' ? '' : val)}>
                      <SelectTrigger data-testid="tenant-switcher" className={`w-56 h-8 text-xs ${isDarkMode ? 'bg-zinc-800/50 border-white/10' : 'border-slate-200'}`}>
                        <SelectValue placeholder="All Tenants (My Org)" />
                      </SelectTrigger>
                      <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                        <SelectItem value="my-org">My Organization</SelectItem>
                        {tenants.map(tenant => (
                          <SelectItem key={tenant.id} value={tenant.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{tenant.name}</span>
                              <span className={`text-xs ml-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                                ({tenant.user_count || 0} users)
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Bulk Import Button - Only for Platform Admins */}
                {isPlatformAdmin && (
                  <Dialog open={isBulkImportOpen} onOpenChange={setIsBulkImportOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-sm">
                        <Upload className="w-3.5 h-3.5 mr-2" />
                        Bulk Import
                      </Button>
                    </DialogTrigger>
                    <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white max-w-lg' : 'bg-white border-slate-200 text-slate-900 max-w-lg'}>
                      <DialogHeader>
                        <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
                          <Upload className="w-5 h-5 text-emerald-400" />
                          Bulk User Import
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                          <p className={`text-sm mb-3 ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                            Upload a CSV file to import multiple users at once. Each row should contain user details.
                          </p>
                          <Button 
                            variant="outline" 
                            onClick={downloadTemplate}
                            className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 text-sm"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download CSV Template
                          </Button>
                        </div>
                        
                        <div className="p-4 bg-zinc-800/50 rounded-lg border-2 border-dashed border-zinc-600">
                          <div className="text-center">
                            <FileText className="w-10 h-10 text-zinc-500 mx-auto mb-2" />
                            <p className="text-zinc-400 text-sm mb-3">Select a CSV file to import</p>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".csv"
                              onChange={handleBulkImport}
                              className="hidden"
                              id="bulk-import-file"
                            />
                            <Button
                              onClick={() => fileInputRef.current?.click()}
                              disabled={bulkImporting}
                              className="bg-emerald-600 hover:bg-emerald-500"
                            >
                              {bulkImporting ? 'Processing...' : 'Select CSV File'}
                            </Button>
                          </div>
                        </div>
                        
                        {bulkImportResults && (
                          <div className={`p-4 rounded-lg ${
                            bulkImportResults.error ? 'bg-red-900/20 border border-red-500/30' : 'bg-zinc-800/50'
                          }`}>
                            <p className="text-zinc-300 font-semibold mb-2">Import Results:</p>
                            {bulkImportResults.error ? (
                              <p className="text-red-400 text-sm">{bulkImportResults.error}</p>
                            ) : (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                  <span className="text-green-400">{bulkImportResults.successful} users imported successfully</span>
                                </div>
                                {bulkImportResults.failed > 0 && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <XCircle className="w-4 h-4 text-red-400" />
                                    <span className="text-red-400">{bulkImportResults.failed} users failed</span>
                                  </div>
                                )}
                                {bulkImportResults.errors?.length > 0 && (
                                  <div className="mt-2 p-2 bg-zinc-900 rounded text-xs">
                                    <p className="text-zinc-400 mb-1">Errors:</p>
                                    {bulkImportResults.errors.slice(0, 5).map((err, i) => (
                                      <p key={i} className="text-red-400">{err}</p>
                                    ))}
                                    {bulkImportResults.errors.length > 5 && (
                                      <p className="text-zinc-500">...and {bulkImportResults.errors.length - 5} more</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                          <p className="text-yellow-400 text-xs">
                            <strong>CSV Format:</strong> email, full_name, password, role, organization_id (optional)
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-user-button" className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani text-sm text-white">
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                  <DialogHeader>
                    <DialogTitle className="font-rajdhani text-xl">Add New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="user-name" className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Full Name</Label>
                      <Input
                        id="user-name"
                        data-testid="user-name-input"
                        value={newUser.full_name}
                        onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                        className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="user-email" className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Email</Label>
                      <Input
                        id="user-email"
                        data-testid="user-email-input"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="user-password" className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Password</Label>
                      <Input
                        id="user-password"
                        data-testid="user-password-input"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="user-role" className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Role</Label>
                      <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                        <SelectTrigger data-testid="user-role-select" className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="analyst">Analyst</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={role.name}>{role.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Tenant selection - only visible for Platform Admins */}
                    {isPlatformAdmin && (
                      <div>
                        <Label htmlFor="user-tenant" className={`text-xs flex items-center gap-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                          <Building2 className="w-3.5 h-3.5 text-violet-400" />
                          Assign to Tenant
                        </Label>
                        <Select 
                          value={newUser.organization_id} 
                          onValueChange={(value) => setNewUser({ ...newUser, organization_id: value })}
                        >
                          <SelectTrigger 
                            data-testid="user-tenant-select" 
                            className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                          >
                            <SelectValue placeholder="Select a tenant..." />
                          </SelectTrigger>
                          <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                            {tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-3 h-3 text-violet-400" />
                                  {tenant.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          Leave empty to assign to your organization
                        </p>
                      </div>
                    )}
                    <Button data-testid="submit-user-button" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-sm">
                      Create User
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            </div>

            {/* Show selected tenant info banner if viewing another tenant */}
            {isPlatformAdmin && viewingTenant && (
              <div className={`mb-4 p-3 rounded-lg border ${isDarkMode ? 'bg-violet-500/10 border-violet-500/30' : 'bg-violet-50 border-violet-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-violet-400" />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                      Viewing: {viewingTenant.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                      {selectedTenantUsers.length} users
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      (View Only)
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleTenantSwitch('')}
                    className="text-xs text-violet-400 hover:text-violet-300"
                  >
                    Back to My Org
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {/* Display selected tenant users or own org users */}
              {(() => {
                const displayUsers = isPlatformAdmin && selectedTenantId ? selectedTenantUsers : users;
                const isViewOnly = isPlatformAdmin && selectedTenantId;
                
                if (displayUsers.length === 0) {
                  return (
                    <div className="text-center py-10">
                      <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400 font-inter text-sm">No users found</p>
                    </div>
                  );
                }
                
                return displayUsers.map((user) => (
                  <div
                    key={user.id}
                    data-testid={`user-item-${user.id}`}
                    className="glass-hover p-4 rounded-lg border border-white/10 hover:border-indigo-500/30 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/30' : 'bg-indigo-100'}`}>
                          <UserCog className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        </div>
                        <div>
                          <p className={`font-inter text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.full_name || user.name || 'Unknown'}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{user.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase ${isDarkMode ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-100 text-indigo-700 border border-indigo-300'}`}>
                          {user.role}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs ${
                          user.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                        {/* Only show delete button if not viewing another tenant */}
                        {!isViewOnly && (
                          <Button
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => handleDeleteUser(user.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="roles" className="mt-5">
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-5">
              <h2 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Role Management</h2>
              
              <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-role-button" className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani text-sm text-white">
                    <Plus className="w-3.5 h-3.5 mr-2" />
                    Add Role
                  </Button>
                </DialogTrigger>
                <DialogContent className={`max-w-3xl max-h-[85vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                  <DialogHeader>
                    <DialogTitle className="font-rajdhani text-xl">Create Custom Role</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateRole} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="role-name" className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Role Name</Label>
                      <Input
                        id="role-name"
                        data-testid="role-name-input"
                        value={newRole.name}
                        onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                        placeholder="e.g., security_auditor"
                        className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="role-display-name" className="text-zinc-300 text-xs">Display Name</Label>
                      <Input
                        id="role-display-name"
                        data-testid="role-display-name-input"
                        value={newRole.display_name}
                        onChange={(e) => setNewRole({ ...newRole, display_name: e.target.value })}
                        placeholder="e.g., Security Auditor"
                        className="bg-zinc-950/50 border-white/10 text-white mt-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <Label className={`text-xs mb-3 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Legacy Permissions</Label>
                      <div className={`grid grid-cols-2 gap-3 max-h-40 overflow-y-auto p-3 rounded-lg ${isDarkMode ? 'bg-zinc-950/30' : 'bg-slate-50'}`}>
                        {ALL_PERMISSIONS.map((permission) => (
                          <div key={permission.id} className="flex items-center gap-2">
                            <Checkbox
                              id={permission.id}
                              checked={newRole.permissions.includes(permission.id)}
                              onCheckedChange={() => handlePermissionToggle(permission.id)}
                              className={isDarkMode ? 'border-white/20' : 'border-slate-300'}
                            />
                            <Label htmlFor={permission.id} className={`text-xs cursor-pointer ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                              {permission.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Granular Page Permissions Matrix */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Table2 className={`w-4 h-4 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                        <Label className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Page-Level Permissions Matrix</Label>
                      </div>
                      <div className={`rounded-lg border overflow-hidden max-h-[300px] overflow-y-auto ${isDarkMode ? 'bg-zinc-950/30 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 z-10">
                            <tr className={isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}>
                              <th className={`text-left py-2 px-3 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Page</th>
                              <th className={`text-center py-2 px-2 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                                <div className="flex flex-col items-center">
                                  <Eye className="w-3 h-3 mb-0.5" />
                                  <span>View</span>
                                </div>
                              </th>
                              <th className={`text-center py-2 px-2 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                                <div className="flex flex-col items-center">
                                  <PenLine className="w-3 h-3 mb-0.5" />
                                  <span>Write</span>
                                </div>
                              </th>
                              <th className={`text-center py-2 px-2 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                                <div className="flex flex-col items-center">
                                  <Edit className="w-3 h-3 mb-0.5" />
                                  <span>Edit</span>
                                </div>
                              </th>
                              <th className={`text-center py-2 px-2 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                                <div className="flex flex-col items-center">
                                  <Trash className="w-3 h-3 mb-0.5" />
                                  <span>Delete</span>
                                </div>
                              </th>
                              <th className={`text-center py-2 px-2 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>All</th>
                            </tr>
                          </thead>
                          <tbody>
                            {PAGE_PERMISSIONS.map((page, idx) => {
                              const pagePerms = newRole.page_permissions[page.id] || { view: false, write: false, edit: false, delete: false };
                              const allEnabled = pagePerms.view && pagePerms.write && pagePerms.edit && pagePerms.delete;
                              
                              return (
                                <tr 
                                  key={page.id} 
                                  className={`${isDarkMode ? 'border-white/5' : 'border-slate-100'} ${idx % 2 === 0 ? (isDarkMode ? 'bg-zinc-900/30' : 'bg-white') : ''}`}
                                >
                                  <td className={`py-2 px-3 font-medium ${isDarkMode ? 'text-zinc-200' : 'text-slate-700'}`}>
                                    {page.label}
                                  </td>
                                  {PERMISSION_ACTIONS.map(action => (
                                    <td key={action} className="text-center py-2 px-2">
                                      <Checkbox
                                        checked={pagePerms[action]}
                                        onCheckedChange={() => handlePagePermissionToggle(page.id, action)}
                                        className={`${isDarkMode ? 'border-white/20' : 'border-slate-300'} ${
                                          action === 'view' ? 'data-[state=checked]:bg-blue-600' :
                                          action === 'write' ? 'data-[state=checked]:bg-emerald-600' :
                                          action === 'edit' ? 'data-[state=checked]:bg-amber-600' :
                                          'data-[state=checked]:bg-red-600'
                                        }`}
                                        data-testid={`perm-${page.id}-${action}`}
                                      />
                                    </td>
                                  ))}
                                  <td className="text-center py-2 px-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setAllPagePermissions(page.id, !allEnabled)}
                                      className={`h-6 w-6 p-0 ${allEnabled 
                                        ? (isDarkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-500') 
                                        : (isDarkMode ? 'text-zinc-500 hover:text-zinc-400' : 'text-slate-400 hover:text-slate-500')
                                      }`}
                                    >
                                      <Grid3X3 className="w-3.5 h-3.5" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Tip: Enabling Write/Edit/Delete automatically enables View for that page.
                      </p>
                    </div>
                    <Button data-testid="submit-role-button" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-sm">
                      Create Role
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {roles.length === 0 ? (
                <div className="text-center py-10">
                  <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 font-inter text-sm">No custom roles found</p>
                </div>
              ) : (
                roles.map((role) => (
                  <div
                    key={role.id}
                    data-testid={`role-item-${role.id}`}
                    className={`p-4 rounded-lg border transition-all ${isDarkMode ? 'glass-hover border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Shield className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                          <p className={`font-rajdhani text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{role.display_name}</p>
                          {role.is_system && (
                            <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'}`}>System</span>
                          )}
                        </div>
                        <p className={`text-xs font-mono mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{role.name}</p>
                        
                        {/* Legacy Permissions */}
                        {role.permissions?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {role.permissions.map((perm) => (
                              <span key={perm} className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                                {perm.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Page Permissions Summary */}
                        {role.page_permissions && Object.keys(role.page_permissions).length > 0 && (
                          <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                            <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Page Permissions:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(role.page_permissions).map(([pageId, perms]) => {
                                const activePerms = Object.entries(perms).filter(([, v]) => v).map(([k]) => k[0].toUpperCase());
                                if (activePerms.length === 0) return null;
                                const pageName = PAGE_PERMISSIONS.find(p => p.id === pageId)?.label || pageId;
                                return (
                                  <span 
                                    key={pageId} 
                                    className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30' : 'bg-violet-100 text-violet-700 border border-violet-200'}`}
                                    title={`${pageName}: ${activePerms.join(', ')}`}
                                  >
                                    {pageName}: {activePerms.join('')}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {!role.is_system && (
                        <Button
                          data-testid={`delete-role-${role.id}`}
                          onClick={() => handleDeleteRole(role.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-5">
          <ImmutableAuditPanel isDarkMode={isDarkMode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Immutable Audit Logs Panel Component
function ImmutableAuditPanel({ isDarkMode }) {
  const [logs, setLogs] = useState([]);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
    verifyChain();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/audit/immutable?limit=50');
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const verifyChain = async () => {
    try {
      const response = await api.get('/audit/immutable/verify');
      setVerification(response.data);
    } catch (error) {
      console.error('Failed to verify chain:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-rajdhani text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <Lock className="w-5 h-5 text-emerald-400" />
            Immutable Audit Logs (Blockchain-Style)
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Tamper-proof audit trail with cryptographic hash chaining
          </p>
        </div>
        <Button onClick={verifyChain} variant="outline" className={isDarkMode ? 'border-white/10' : ''}>
          <Shield className="w-4 h-4 mr-2" /> Verify Chain
        </Button>
      </div>

      {/* Chain Verification Status */}
      {verification && (
        <div className={`p-4 rounded-xl mb-6 ${verification.chain_valid ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className="flex items-center gap-3">
            {verification.chain_valid ? (
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-red-400" />
            )}
            <div>
              <p className={`font-semibold ${verification.chain_valid ? 'text-emerald-400' : 'text-red-400'}`}>
                Chain Integrity: {verification.chain_valid ? 'VERIFIED' : 'COMPROMISED'}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                {verification.total_entries} entries verified • Last check: {new Date(verification.verification_timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          {verification.tampered_entries?.length > 0 && (
            <div className="mt-3 text-sm text-red-400">
              <strong>Tampered entries detected:</strong> {verification.tampered_entries.map(t => `#${t.sequence}`).join(', ')}
            </div>
          )}
        </div>
      )}

      {logs.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No immutable audit logs yet</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {logs.map((log, index) => (
            <div key={log.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                  #{log.sequence}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{log.action}</span>
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                      {log.resource_type}/{log.resource_id}
                    </span>
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                    {log.actor_email} • {new Date(log.timestamp).toLocaleString()}
                  </div>
                </div>
                <div className={`text-xs font-mono ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                  <Hash className="w-3 h-3 inline mr-1" />
                  {log.entry_hash?.substring(0, 12)}...
                </div>
                {log.chain_valid !== false ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
